// react
import { MouseEvent, useContext, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// types
import {
	Account,
	Category,
	Component,
	Contract,
	Entity,
	ErrorCause,
	ExpenseType,
	Loan,
	MetadataField,
	Transaction,
	TransactionWithDaEquivalent,
	TransactionType,
	WithdrawalApplication,
} from '../../../@types';
import { financiersWbg } from '../../../@types/unions';
import { Size, TableCell, TDocumentDefinitions } from 'pdfmake/interfaces';

// context
import { ContextBanner, ContextUser } from '../../../App.context';
import { ContextProject, ContextUserProjectPermissions } from '../../pages/Project/Project.context';
import {
	ContextSoeFilters,
	ContextSoeTransactions,
} from '../../pages/Project/Reports/Soe/Soe.context';

// mui
import { Alert, Grid } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// fields
import { Input } from '../../fields/Input/Input';
import { RadioButtons } from '../../fields/RadioButtons/RadioButtons';

// ui
import { TableInfoParams } from '../../ui/TableInfoParams/TableInfoParams';

// tables
import { getHeaderLabelMap } from '../../tables/basic/TableBasicSoe/TableBasicSoe.headers';

// icons
import {
	ArticleOutlined as IconArticle,
	InsertDriveFileOutlined as IconInsertDriveFile,
} from '@mui/icons-material';

// utils
import { addDaEquivalentPropsToTransactions } from '../../../utils/transactions';
import { downloadFile, escapeCsvValue, jsonToCsv } from '../../../utils/files';
import { formatNumber } from '../../../utils/format';
import { formatDate } from '../../../utils/dates';
import { translateStatus, translateTransactionType } from '../../../utils/translations';
import { formatCurrency } from '../../../utils/currency';

// props
export interface DialogExportSoeReportProps {
	accounts: Account[];
	categories: Category[];
	components: Component[];
	expenseTypes: ExpenseType[];
	isOpen: boolean;
	loans: Loan[];
	onClose: () => void;
	onSuccess?: () => void;
}

export const DialogExportSoeReport = ({
	accounts,
	categories,
	components,
	expenseTypes,
	isOpen,
	loans,
	onClose,
	onSuccess,
}: DialogExportSoeReportProps) => {
	// hooks
	const { t } = useTranslation();

	// constants
	const emptyCell = '';

	// state
	const [error, setError] = useState<{ message: string; errors?: string[] } | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [searchParams] = useSearchParams();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);
	const { filters, filtersValues } = useContext(ContextSoeFilters);
	const { headerSelections } = useContext(ContextSoeTransactions);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// params
	const paramId = searchParams.get('id');
	const paramReadOnlyFields = searchParams.get('readOnlyFields')?.split(',');
	const paramWithdrawalApplicationNew = searchParams.get('withdrawalApplicationNew');

	// vars
	const idForm = 'form-export-soe-report';
	const fromAccount = accounts.find((account) => account.id === Number(filtersValues.fromAccount));
	const loan = loans.find((loan) => loan.id === Number(filtersValues.loan));

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		// clear error
		setError(null);

		// set loading
		setIsLoading(true);

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());
		const dateToday = formatDate({ date: new Date(), format: 'YYYY-MM-DD' });
		let fileName = `${project?.idWbg}-${loan?.code}-${dateToday}-soe`;

		// set pdf options
		const pdfOptions: Omit<TDocumentDefinitions, 'content'> = {
			pageOrientation: 'landscape',
			pageSize: 'A3',
		};

		try {
			// construct fetch transactions url
			const populate = [
				'category',
				'component',
				'contract',
				'designatedAccount',
				'expenseType',
				'fromAccount',
				'fromEntity',
				'payments.conversions',
				'payments.loan',
				'toAccount',
				'toEntity',
				'withdrawalApplication',
			];

			let urlFetchTransactions = `${
				process.env.REACT_APP_API_URL
			}/transactions?status=!REVERTED&populate=${populate.join(',')}`;

			// define filters keys
			const filtersKeys = Object.keys(filtersValues);

			// define withdrawal applicationId id
			let withdrawalApplicationId: number | undefined = undefined;

			// add each filter value to url
			for (let i = 0; i < filtersKeys.length; i++) {
				const key = filtersKeys[i];

				if (key === 'fromAccount') {
					urlFetchTransactions += `&or=${encodeURIComponent(`fromAccount:=:${filtersValues[key]},designatedAccount:=:${filtersValues[key]},(toAccount:=:${filtersValues[key]},type:=:PAYMENT_REFUND)`)}`;
				} else if (Array.isArray(filtersValues[key])) {
					if (filtersValues[key].length) {
						urlFetchTransactions += `&${key}=${(filtersValues[key] as string[]).join(',')}`;
					}
				} else if (filtersValues[key]) {
					urlFetchTransactions += `&${key}=${filtersValues[key]}`;
				}
			}

			// add id param
			if (paramId) {
				urlFetchTransactions += `&id=${paramId}`;
			}

			// fetch transaction ids
			const fetchGetTransactionIds = await fetch(`${urlFetchTransactions}&fields=transaction.id`, {
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});

			const resGetTransactionIds = await fetchGetTransactionIds.json();

			if (resGetTransactionIds.error) {
				throw new Error(resGetTransactionIds.error.message, {
					cause: {
						id: resGetTransactionIds.error.id,
					},
				});
			}

			if (entries['withdrawalApplication']) {
				// update file name with withdrawalApplication id
				fileName = `${project?.idWbg}-${loan?.code}-${
					entries['withdrawalApplication']
				}-${dateToday}-soe-${t('summary')}`;

				const getWithdrawalApplication = await fetch(
					`${process.env.REACT_APP_API_URL}/withdrawal-applications?idExternal=${String(
						entries['withdrawalApplication']
					)}&project=${project?.id}`,
					{
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					}
				);

				const resGetWithdrawalApplication = await getWithdrawalApplication.json();

				if (resGetWithdrawalApplication.error) {
					throw new Error(resGetWithdrawalApplication.error.message, {
						cause: {
							id: resGetWithdrawalApplication.error.id,
						},
					});
				}
				if (resGetWithdrawalApplication.data.length) {
					withdrawalApplicationId = resGetWithdrawalApplication.data[0].id;
				} else {
					const postWithdrawalApplication = await fetch(
						`${process.env.REACT_APP_API_URL}/withdrawal-applications/`,
						{
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
								Authorization: `Bearer ${accessToken}`,
								User: String(idToken),
							},
							body: JSON.stringify({
								idExternal: String(entries['withdrawalApplication']),
								project: project?.id,
								user: user?.id ? user?.id : undefined,
							}),
						}
					);

					const resPostWithdrawalApplication = await postWithdrawalApplication.json();

					if (resPostWithdrawalApplication.data) {
						withdrawalApplicationId = resPostWithdrawalApplication.data.id;
					}

					if (resPostWithdrawalApplication.error) {
						throw new Error(resPostWithdrawalApplication.error.message, {
							cause: {
								id: resPostWithdrawalApplication.error.id,
							},
						});
					}
				}

				const reqUpdateTransaction = await fetch(`${process.env.REACT_APP_API_URL}/transactions`, {
					method: 'PATCH',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify(
						resGetTransactionIds.data.map((tx: Transaction) => {
							return {
								id: tx.id,
								withdrawalApplication: withdrawalApplicationId,
							};
						})
					),
				});
				const resUpdateTransactions = await reqUpdateTransaction.json();

				if (resUpdateTransactions.error) {
					throw new Error(resUpdateTransactions.error.message, {
						cause: {
							id: resUpdateTransactions.error.id,
						},
					});
				}

				if (resUpdateTransactions.data.failed.length) {
					throw new Error(
						`${resUpdateTransactions.data.failed.length} transactions failed to update.`,
						{
							cause: {
								id: 'TRANSACTION_PATCH_INCOMPLETE',
								res: resUpdateTransactions,
							},
						}
					);
				}
			}

			// fetch full transactions
			const fetchGetTransactions = await fetch(urlFetchTransactions, {
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});

			const resGetTransactions = await fetchGetTransactions.json();

			if (resGetTransactions.error) {
				throw new Error(resGetTransactions.error.message, {
					cause: {
						id: resGetTransactions.error.id,
					},
				});
			}

			const transactions = addDaEquivalentPropsToTransactions({
				loanId: Number(filtersValues.loan),
				transactions: resGetTransactions.data,
			});

			// Get header label map from centralized definition
			const headerLabelMap = getHeaderLabelMap({ filters, t });

			// format transactions for csv
			const rows = transactions.map((transaction: TransactionWithDaEquivalent) => {
				const contract = transaction.contract as Contract;
				const categoryName = (transaction.category as Category)?.name;
				const hasComments = Array.isArray(transaction.comments) && transaction.comments.length > 0;
				const isPayment = transaction.type === 'PAYMENT';

				const loanFilter = filters?.find((f) => f.name === 'loan');

				let percentFinancedLabel = t('percentFinanced');
				if (loanFilter) {
					const loanOption = loanFilter.options?.find((opt) => opt.value === loanFilter.value);
					if (
						loanOption?.MenuItemProps?.financier &&
						financiersWbg.includes(loanOption?.MenuItemProps?.financier)
					) {
						percentFinancedLabel = t('percentFinancedByFinancier', {
							financier: loanOption.MenuItemProps?.financier,
						});
					}
				}

				const isDaCurrency = transaction.currency === transaction.currencyDa;

				// Build full data object with all possible columns
				const allData: Record<string, string | number | null | undefined> = {
					ID: transaction.id.toString(),
					_categoryName: categoryName, // Internal field for grouping
					[t('type')]: translateTransactionType(transaction.type, t),
					[t('to')]: (() => {
						if (transaction.type === 'OPEX') {
							if (transaction.metadata?.vendor) {
								return transaction.metadata?.vendor;
							}
						} else if ((transaction.toEntity as Entity)?.name) {
							return (transaction.toEntity as Entity)?.name;
						}

						return emptyCell;
					})(),
					[t('from')]: (transaction.fromEntity as Entity)?.name || emptyCell,
					[t('fromAccount')]: (transaction.fromAccount as Account)?.name || emptyCell,
					[t('designatedAccount')]: (transaction.designatedAccount as Account)?.name || emptyCell,
					[t('contract')]: contract?.num || emptyCell,
					[t('contractTotalAmount')]: (() => {
						const toEntity = transaction.toEntity as Entity;
						const isBeneficiary = toEntity?.type === 'BENEFICIARY';

						if (isBeneficiary) {
							const parsedMetadata: MetadataField[] = Array.isArray(toEntity?.metadata)
								? (toEntity.metadata as MetadataField[])
								: [];
							const metadataTotalBenefits = parsedMetadata.find((m) => m.name === 'totalBenefits');
							const metadataCurrency = parsedMetadata.find((m) => m.name === 'currency');
							if (metadataTotalBenefits?.value) {
								return metadataCurrency?.value
									? `${formatNumber(Number(metadataTotalBenefits?.value), {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
											useGrouping: entries['format'] === 'pdf',
										})} ${metadataCurrency.value}`
									: String(metadataTotalBenefits?.value);
							}
							return emptyCell;
						}

						if (contract?.amounts?.length) {
							const amounts = contract.amounts.map(
								(amt) =>
									`${formatNumber(amt.amount, {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
										useGrouping: entries['format'] === 'pdf',
									})} ${amt.currency}`
							);
							return entries['format'] === 'pdf' ? amounts.join('\n') : amounts.join(' / ');
						}

						return emptyCell;
					})(),
					[t('stepId')]: contract?.idStep || emptyCell,
					[t('component')]: (transaction.component as Component)?.name || emptyCell,
					[t('category')]: (transaction.category as Category)?.name || emptyCell,
					[t('expenseType')]: (transaction.expenseType as ExpenseType)?.name || emptyCell,
					[t('invoiceAmount')]: isPayment
						? transaction.metadata?.invoiceAmount || emptyCell
						: t('na'),
					[t('invoiceCurrency')]: isPayment
						? transaction.metadata?.invoiceCurrency || emptyCell
						: t('na'),
					[percentFinancedLabel]: (() => {
						return emptyCell;
					})(),
					[t('currency')]: transaction.currency || emptyCell,
					[t('amount')]: (() => {
						if (transaction.amountLoan) {
							if (transaction.type === 'PAYMENT_REFUND') {
								return transaction.amountLoan * -1;
							}
							return transaction.amountLoan;
						}
						return emptyCell;
					})(),
					[t('tax')]: transaction.taxLoan || 0,
					[`${t('currency')} (${t('daEquivalent')})`]: transaction.currencyDa || emptyCell,
					[`${t('amount')} (${t('daEquivalent')})`]: (() => {
						let amount: number | string = emptyCell;

						// use DA equivalent amount if available
						if (transaction.amountLoanDaEquivalent) {
							amount = transaction.amountLoanDaEquivalent;

							// display refunds as negative values
							if (transaction.type === 'PAYMENT_REFUND') {
								amount = amount * -1;
							}
						} else if (transaction.amountLoan && isDaCurrency) {
							// use original amount if no DA equivalent but transaction currency is same as DA currency
							amount = transaction.amountLoan;
						}

						return amount;
					})(),
					[`${t('tax')} (${t('daEquivalent')})`]: (() => {
						let tax: number | string = emptyCell;
						if (transaction.taxLoanDaEquivalent) {
							tax = transaction.taxLoanDaEquivalent;
						} else if (transaction.taxLoan && isDaCurrency) {
							tax = transaction.taxLoan;
						}
						return tax;
					})(),
					[t('totalPaidFromDa')]: (() => {
						let total: number | string = emptyCell;
						const amount =
							transaction.amountLoanDaEquivalent ?? (isDaCurrency ? transaction.amountLoan : null);
						const tax =
							transaction.taxLoanDaEquivalent ?? (isDaCurrency ? transaction.taxLoan : null);
						if (transaction.type !== 'PAYMENT_REFUND' && amount !== null && amount !== undefined) {
							total = amount + (tax || 0);
						}
						return total;
					})(),
					[t('exchangeRate')]:
						transaction.exchangeRateDa !== null ? transaction.exchangeRateDa : emptyCell,
					[t('exchangeRateBaseCurrency')]: transaction.exchangeRateBaseCurrency || emptyCell,
					[t('exchangeRateQuoteCurrency')]: transaction.exchangeRateQuoteCurrency || emptyCell,
					[t('withdrawalApplication')]:
						(transaction.withdrawalApplication as WithdrawalApplication)?.idExternal || emptyCell,
					[t('dateOfPayment')]: formatDate({ date: transaction.date, format: 'YYYY-MM-DD' }),
					[t('status')]: translateStatus(transaction.status, t),
					[t('comments')]: hasComments
						? transaction.comments.map((c) => c.comment).join('\n\n')
						: emptyCell,
					[t('created')]: formatDate({ date: transaction.created, format: 'YYYY-MM-DD' }),
					[t('modified')]: formatDate({ date: transaction.modified, format: 'YYYY-MM-DD' }),
				};

				// Filter to only include visible headers based on headerSelections
				const headersVisible = headerSelections.filter((h) => h.checked).map((h) => h.key);

				// Build filtered data in the order of visible headers
				const filteredData: Record<string, string | number | null | undefined> = {
					_categoryName: allData._categoryName, // Always include for internal grouping (will be removed before export)
				};
				headersVisible.forEach((headerKey) => {
					const label = headerLabelMap[headerKey];
					if (label && allData[label] !== undefined) {
						filteredData[label] = allData[label];
					}
				});

				return filteredData;
			});

			let statDateFrom = t('na');
			let statDateTo = t('na');

			if (filtersValues.dateStart) {
				statDateFrom = formatDate({
					date: new Date(filtersValues.dateStart as string),
					format: 'YYYY-MM-DD',
				});
			}

			if (filtersValues.dateEnd) {
				statDateTo = formatDate({
					date: new Date(filtersValues.dateEnd as string),
					format: 'YYYY-MM-DD',
				});
			}

			// Build header information (used by both PDF and CSV)
			const headerInfo = {
				title: t('soe'),
				subtitle: null,
				stats: [
					{
						label: t('project'),
						value: `${project?.name || ''}${project?.idWbg ? ` (${project.idWbg})` : ''}`,
					},
					{
						label: t('from'),
						value: statDateFrom,
					},
					{
						label: t('to'),
						value: statDateTo,
					},
					{ label: t('loanNo'), value: loan?.code || t('na') },
					{
						label: t('applicationNo'),
						value: entries['withdrawalApplication'] || withdrawalApplicationId || t('na'),
					},
					{ label: t('designatedAccount'), value: fromAccount?.name || t('na') },
					...(filtersValues.category
						? [
								{
									label: t('category'),
									value: (() => {
										// Join multiple selected categories
										const categoryFilter = filtersValues.category;
										const categoryArray = Array.isArray(categoryFilter)
											? categoryFilter
											: [categoryFilter];

										const matchedCategories = categories
											.filter(
												(c) =>
													categoryArray.includes(c.id.toString()) ||
													categoryArray.includes(String(c.id))
											)
											.map((c) => c.name)
											.join(' / ');

										return matchedCategories || null;
									})(),
								},
							]
						: []),
				],
			};

			// Always group by category for exports
			const isGroupedByCategory = true;

			const calculateTotalPaidFromDa = (txns: TransactionWithDaEquivalent[]) => {
				return txns.reduce((sum, transaction) => {
					if (transaction.amountLoanDaEquivalent !== null && transaction.currencyDa) {
						const amount = transaction.amountLoanDaEquivalent || 0;
						const tax = transaction.taxLoanDaEquivalent || 0;
						return sum + amount + tax;
					}
					return sum;
				}, 0);
			};

			const transactionsByCategory = isGroupedByCategory
				? transactions.reduce(
						(acc, transaction) => {
							const categoryName = (transaction.category as Category)?.name || t('uncategorized');
							if (!acc[categoryName]) {
								acc[categoryName] = [];
							}
							acc[categoryName].push(transaction);
							return acc;
						},
						{} as Record<string, typeof transactions>
					)
				: {};

			const rowsFormatted = rows.map((row) => {
				const rowFormatted: Record<string, string | number | null | undefined> = {};
				Object.entries(row).forEach(([key, value]) => {
					if (typeof value === 'number') {
						rowFormatted[key] = formatNumber(value, {
							maximumFractionDigits: 2,
							minimumFractionDigits: 2,
							useGrouping: entries['format'] === 'pdf',
						});
					} else {
						rowFormatted[key] = value;
					}
				});
				return rowFormatted;
			});

			if (entries['format'] === 'pdf') {
				const tables: Array<{
					pageBreak?: 'before';
					maxCols: number;
					header?: typeof headerInfo;
					table: { body: TableCell[][]; widths: Size[] };
				}> = [];

				// Helper to create a PDF table from rows and transactions
				const createPdfTable = (
					tableRows: typeof rows,
					tableTransactions: TransactionWithDaEquivalent[]
				) => {
					const tableBody: TableCell[][] = [];
					const widths: Size[] = [];

					if (!tableRows.length) return null;

					const headers = Object.keys(tableRows[0]).filter((key) => key !== '_categoryName');
					tableBody.push(headers);

					const totalPaidFromDaIndex = headers.findIndex(
						(h) => h === t('totalPaidFromDa') || h.includes('Total Paid')
					);

					tableRows.forEach((row) => {
						const dataRow = headers.map((header) => {
							const value = row[header];
							return value != null ? String(value) : '';
						});
						tableBody.push(dataRow);
					});

					const totalPaidFromDa = calculateTotalPaidFromDa(tableTransactions);
					const daCurrency = tableTransactions.find((tx) => tx.currencyDa)?.currencyDa || '';

					const subtotalRow = headers.map((header, idx) => {
						if (idx === 0) {
							return { text: t('total'), bold: true, fillColor: '#f0f0f0' };
						} else if (idx === totalPaidFromDaIndex && totalPaidFromDaIndex !== -1) {
							return {
								text: daCurrency
									? `${formatCurrency({ currency: daCurrency, value: totalPaidFromDa })}`
									: emptyCell,
								bold: true,
								fillColor: '#f0f0f0',
							};
						}
						return { text: '', fillColor: '#f0f0f0' };
					});
					tableBody.push(subtotalRow);

					headers.forEach(() => {
						widths.push('auto' as Size);
					});

					return { body: tableBody, widths };
				};

				if (isGroupedByCategory) {
					Object.entries(transactionsByCategory).forEach(
						([categoryName, categoryTransactions], index) => {
							const categoryFormattedRows = rowsFormatted.filter(
								(row) => row._categoryName === categoryName
							);
							const table = createPdfTable(categoryFormattedRows, categoryTransactions);

							if (table) {
								const header = {
									...headerInfo,
									stats: headerInfo.stats?.map((stat) =>
										stat.label === t('category')
											? { label: t('category'), value: categoryName }
											: stat
									),
								};
								tables.push({
									pageBreak: index > 0 ? 'before' : undefined,
									maxCols: 19,
									header,
									table,
								});
							}
						}
					);
				} else {
					const table = createPdfTable(rowsFormatted, transactions);
					if (table) {
						tables.push({
							maxCols: 19,
							table,
						});
					}
				}

				const payload = {
					options: pdfOptions,
					...(isGroupedByCategory ? {} : { header: headerInfo }),
					tables,
					title: t('soe'),
				};

				const reqPostPdf = await fetch(`${process.env.REACT_APP_API_URL}/services/pdfs`, {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(payload),
				});

				const resPostPdf = await reqPostPdf.json();

				if (resPostPdf.error) {
					throw new Error(resPostPdf.error.message, {
						cause: {
							id: resPostPdf.error.id,
						},
					});
				}

				// download as pdf file
				downloadFile({
					data: new Uint8Array(resPostPdf.data.blob.data).buffer,
					name: `${fileName}.pdf`,
					type: 'application/pdf',
				});
			} else if (entries['format'] === 'csv') {
				let csvContent = '';

				// Helper to create CSV rows with total
				const createCsvRows = (
					csvRows: typeof rowsFormatted,
					tableTransactions: TransactionWithDaEquivalent[]
				) => {
					const cleanedRows = csvRows.map(({ _categoryName, ...rest }) => rest);
					const totalPaidFromDa = calculateTotalPaidFromDa(tableTransactions);
					const daCurrency = tableTransactions.find((tx) => tx.currencyDa)?.currencyDa || '';

					const totalRow: Record<string, string | number> = {};
					if (cleanedRows.length > 0) {
						const firstKey = Object.keys(cleanedRows[0])[0];
						totalRow[firstKey] = t('total');

						const totalPaidColumnKey = Object.keys(cleanedRows[0]).find(
							(key) => key.toLowerCase().includes('total') && key.toLowerCase().includes('paid')
						);
						if (totalPaidColumnKey) {
							totalRow[totalPaidColumnKey] = daCurrency
								? formatNumber(totalPaidFromDa, {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
										useGrouping: false,
									})
								: emptyCell;
						}
					}
					cleanedRows.push(totalRow);

					return cleanedRows;
				};

				// Helper to build header lines for CSV
				const buildCsvHeaderLines = (categoryName?: string) => {
					const headerLines: string[] = [];

					if (headerInfo.title) {
						headerLines.push(escapeCsvValue(headerInfo.title));
					}

					if (headerInfo.subtitle) {
						headerLines.push(escapeCsvValue(headerInfo.subtitle));
					}

					if (headerInfo.stats) {
						headerInfo.stats.forEach((item) => {
							// Skip category stat if categoryName is provided
							if (categoryName && item.label === t('category')) {
								return;
							}
							// Format as two-column CSV row: label, value
							headerLines.push(
								`${escapeCsvValue(item.label)},${escapeCsvValue(String(item.value))}`
							);
						});
					}

					if (categoryName) {
						headerLines.push(`${escapeCsvValue(t('category'))},${escapeCsvValue(categoryName)}`);
					}

					return headerLines;
				};

				if (isGroupedByCategory) {
					Object.entries(transactionsByCategory).forEach(
						([categoryName, categoryTransactions], index) => {
							const categoryFormattedRows = rowsFormatted.filter(
								(row) => row._categoryName === categoryName
							);

							if (categoryFormattedRows.length) {
								if (index > 0) {
									csvContent += '\n\n';
								}

								const headerLines = buildCsvHeaderLines(categoryName);
								const cleanedRows = createCsvRows(categoryFormattedRows, categoryTransactions);

								csvContent += headerLines.join('\n');
								csvContent += '\n\n';
								csvContent += jsonToCsv({ array: cleanedRows });
							}
						}
					);
				} else {
					const headerLines = buildCsvHeaderLines();
					const cleanedRows = createCsvRows(rowsFormatted, transactions);

					csvContent = headerLines.join('\n');
					csvContent += '\n\n';
					csvContent += jsonToCsv({ array: cleanedRows });
				}

				// download as csv file
				downloadFile({
					data: csvContent,
					name: `${fileName}.csv`,
					type: 'text/csv',
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.reportDownloaded'),
				severity: 'success',
			});

			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause) {
				if (cause.id === 'WITHDRAWAL_APPLICATION_ID_DUPLICATE') {
					message = t('error.withdrawalApplicationIdDuplicate', {
						idExternal: entries['withdrawalApplication'],
					});
				}

				if (cause.id === 'TRANSACTION_PATCH_INCOMPLETE') {
					message = t('error.transactionsPatchIncomplete', {
						failed: cause.res.data.failed.length,
					});
				}
			}

			// set error
			setError({
				message,
			});
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('download'),
					form: idForm,
					loading: isLoading,
					type: 'submit',
					variant: 'contained',
				},
			]}
			hasClose={true}
			isOpen={isOpen}
			onClose={onClose}
			title={{
				children: t('exportSoe'),
				variant: 'h3',
			}}>
			<Grid item xs={12}>
				<form id={idForm} name="formExportSoeReport" onSubmit={handleSubmit}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TableInfoParams
								amountMax={filtersValues.amountMax as string}
								amountMin={filtersValues.amountMin as string}
								categories={categories.filter((c) =>
									filtersValues.category.includes(c.id.toString())
								)}
								components={components.filter((c) =>
									filtersValues.component.includes(c.id.toString())
								)}
								currency={filtersValues.currency as string}
								dateEnd={filtersValues.dateEnd as string}
								dateStart={filtersValues.dateStart as string}
								expenseTypes={expenseTypes.filter((e) =>
									filtersValues.expenseType.includes(e.id.toString())
								)}
								fromAccount={fromAccount}
								loan={loan}
								types={filtersValues.type as TransactionType[]}
							/>
						</Grid>
						{userProjectPermissions?.withdrawalApplication?.create && (
							<Grid item xs={12}>
								<Input
									id={`${idForm}-withdrawal-application`}
									label={t('withdrawalApplication')}
									name="withdrawalApplication"
									defaultValue={paramWithdrawalApplicationNew || ''}
									InputProps={{
										inputProps: {
											pattern: "^[a-zA-Z0-9\\(\\)'\\/\\.,:?+\\-]*$",
											title:
												'Only alphanumeric characters and ( ) / . , : ? + - special characters are allowed without spaces.',
										},
										readOnly: paramReadOnlyFields?.includes('withdrawalApplication') || false,
									}}
									maxLength={20}
									helperText={t(`borrowerReference#`)}
								/>
							</Grid>
						)}
						<Grid item xs={12}>
							<Alert severity="info">{t('alert.existingWithdrawalApplications')}</Alert>
						</Grid>
						<Grid item xs={12}>
							<RadioButtons
								defaultValue={'csv'}
								id={`${idForm}-export-file-format`}
								inputs={[
									{
										icon: <IconArticle />,
										label: t('csv'),
										value: 'csv',
									},
									{
										icon: <IconInsertDriveFile />,
										label: t('pdf'),
										value: 'pdf',
									},
								]}
								label={t('format')}
								name="format"
								required={true}
							/>
						</Grid>
						{error && (
							<Grid item xs={12}>
								<Alert severity="error">{error.message}</Alert>
							</Grid>
						)}
					</Grid>
				</form>
			</Grid>
		</DialogActions>
	);
};
